'use client';
import type { CSSProperties, ReactElement, RefObject } from 'react';
import { useMemo } from 'react';
import { ReactFlow, type Edge, type Node, type NodeMouseHandler, type OnMove, type ReactFlowInstance, } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { GraphArrowPanel, GraphControlsOverlay, GraphMiniMapPanel, GraphNavDirection, GraphZoomPanel, GridPatternBackground, GRAPH_MINIMAP_HEIGHT, GRAPH_MINIMAP_WIDTH, } from '../Cmarkdowngraphcontrols';
import { FLOWCHART_COLORS } from './flowchart-constants';
import { FlowchartEdge } from './FlowchartEdge';
import { FlowchartGroupNode } from './FlowchartGroupNode';
import { FlowchartNode } from './FlowchartNode';
import type { LayoutResult } from './layout-flowchart';
import type { FlowchartEdgeData, FlowchartNodeData } from './flowchart-types';
import { dataEl, type DataElScope } from '@/utils/dataEl';
const CSS_VALUE = {
    Transparent: 'transparent',
} as const;
const FIT_VIEW_PADDING = 0.32;
export const FLOWCHART_CONTAINER_HEIGHT = 520;
const MIN_ZOOM = 0.2;
const MAX_ZOOM = 2;
const FIT_VIEW_OPTIONS = {
    padding: FIT_VIEW_PADDING,
} as const;
/**
 * Ambient green glow behind the canvas, biased to the top where the start
 * terminator settles. React Flow's panes and the dotted grid paint transparent
 * over the wrapper, so this radial sits behind both — the flow reads as
 * originating from a lit source, echoing the landing stage's mint radial. Soft
 * and screen-fixed (it does not chase nodes as the camera pans), so it stays
 * ambient depth rather than a spotlight.
 */
const FLOWCHART_BACKDROP_GLOW = 'radial-gradient(58% 46% at 50% 24%, rgba(102, 254, 193, 0.13), rgba(102, 254, 193, 0) 72%)';
const REACT_FLOW_WRAPPER_STYLE: CSSProperties = {
    position: 'relative',
    flex: '1 1 auto',
    minHeight: FLOWCHART_CONTAINER_HEIGHT,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundImage: FLOWCHART_BACKDROP_GLOW,
};
const REACT_FLOW_STYLE: CSSProperties = {
    width: '100%',
    flex: 1,
    minHeight: 0,
};
/** React Flow node type for the non-interactive subgraph backdrop container. */
const GROUP_NODE_TYPE = 'flowchartGroup';
const PRO_OPTIONS = { hideAttribution: true } as const;
const MINIMAP_NODE_STROKE_COLOR = 'rgba(255, 255, 255, 0.4)';
const MINIMAP_MASK_COLOR = 'rgba(10, 14, 22, 0.22)';
/** Minimap node tint — the node's accent color, falling back to the base hue. */
function getMinimapNodeColor(node: Node): string {
    const accent = typeof node.data?.accentColor === 'string' ? node.data.accentColor : null;
    return accent ?? FLOWCHART_COLORS[0];
}
interface FlowchartCanvasProps {
    controlsScaleStyle: CSSProperties | undefined;
    dataElScope: DataElScope;
    hasContainerSize: boolean;
    isMinimapVisible: boolean;
    layout: LayoutResult;
    reactFlowInstance: ReactFlowInstance<Node<FlowchartNodeData>, Edge<FlowchartEdgeData>> | null;
    reactFlowWrapperRef: RefObject<HTMLDivElement | null>;
    sideColumn: ReactElement;
    zoomLevel: number;
    onInit: (instance: ReactFlowInstance<Node<FlowchartNodeData>, Edge<FlowchartEdgeData>>) => void;
    onMove: OnMove;
    onNavigate: (direction: GraphNavDirection) => void;
    onNodeClick: NodeMouseHandler<Node<FlowchartNodeData>>;
    onNodeMouseEnter: NodeMouseHandler<Node<FlowchartNodeData>>;
    onNodeMouseLeave: NodeMouseHandler<Node<FlowchartNodeData>>;
    onPaneClick: () => void;
    onZoomChange: (targetZoom: number) => void;
    onZoomIn: () => void;
    onZoomOut: () => void;
}
export function FlowchartCanvas({ controlsScaleStyle, dataElScope, hasContainerSize, isMinimapVisible, layout, reactFlowInstance, reactFlowWrapperRef, sideColumn, zoomLevel, onInit, onMove, onNavigate, onNodeClick, onNodeMouseEnter, onNodeMouseLeave, onPaneClick, onZoomChange, onZoomIn, onZoomOut, }: FlowchartCanvasProps): ReactElement {
    // Built here rather than at module scope, and memoised so React Flow still
    // sees one stable identity for the life of the canvas. These node and edge
    // components render markdown labels, which puts them on the markdown render
    // cycle (label -> Cmarkdown -> block renderer -> Cmarkdownflowchart -> this
    // file); reading them while the modules are still evaluating can hand back
    // `undefined`. By render time the graph has settled and they are defined.
    const nodeTypes = useMemo(() => ({ flowchartCard: FlowchartNode, [GROUP_NODE_TYPE]: FlowchartGroupNode }), []);
    const edgeTypes = useMemo(() => ({ flowchartDashed: FlowchartEdge }), []);
    return (
      <div data-el={dataEl(dataElScope, 'FlowchartCanvas/Root')} ref={reactFlowWrapperRef} style={REACT_FLOW_WRAPPER_STYLE}>
        {hasContainerSize ? (<ReactFlow<Node<FlowchartNodeData>, Edge<FlowchartEdgeData>> style={REACT_FLOW_STYLE} nodes={layout.nodes} edges={layout.edges} nodeTypes={nodeTypes} edgeTypes={edgeTypes} fitView fitViewOptions={FIT_VIEW_OPTIONS} nodesDraggable={false} nodesConnectable={false} nodesFocusable={false} edgesFocusable={false} elementsSelectable={false} panOnDrag panOnScroll zoomOnScroll zoomOnPinch zoomOnDoubleClick={false} minZoom={MIN_ZOOM} maxZoom={MAX_ZOOM} preventScrolling onInit={onInit} onMove={onMove} onNodeClick={onNodeClick} onNodeMouseEnter={onNodeMouseEnter} onNodeMouseLeave={onNodeMouseLeave} onPaneClick={onPaneClick} proOptions={PRO_OPTIONS}>
            <GridPatternBackground />
            <GraphZoomPanel zoomLevel={zoomLevel} minZoom={MIN_ZOOM} maxZoom={MAX_ZOOM} disabled={!reactFlowInstance} accentColor={FLOWCHART_COLORS[0]} scaleStyle={controlsScaleStyle} onZoomIn={onZoomIn} onZoomOut={onZoomOut} onZoomChange={onZoomChange}/>
            <GraphControlsOverlay scaleStyle={controlsScaleStyle} sideColumn={sideColumn}>
              <GraphArrowPanel onNavigate={onNavigate}/>
              {isMinimapVisible ? (<GraphMiniMapPanel width={GRAPH_MINIMAP_WIDTH} height={GRAPH_MINIMAP_HEIGHT} nodeColor={getMinimapNodeColor} nodeStrokeColor={MINIMAP_NODE_STROKE_COLOR} nodeStrokeWidth={1.5} nodeBorderRadius={4} maskColor={MINIMAP_MASK_COLOR} bgColor={CSS_VALUE.Transparent} pannable zoomable/>) : null}
            </GraphControlsOverlay>
          </ReactFlow>) : null}
      </div>
    );
}
